// Portfolio Dashboard export: turns the backend's sheet-shaped JSON into the reviewed
// workbook. The backend sends column headers verbatim, so nothing here renames a field --
// if a column looks wrong, the Data Dictionary and the backend payload are the source of
// truth, not this file.
//
// The point of the export is that Excel and Sheets can use it without cleaning: real dates
// and numbers rather than pre-formatted strings, one header row, no merged cells, an
// autofilter and frozen header on every sheet.

import ExcelJS from "exceljs"
import { saveAs } from "file-saver"

export type PortfolioExportPayload = Record<string, Record<string, unknown>[]>

// Sheet order in the workbook: data first, context last.
const SHEET_ORDER = [
  "Run Info",
  "Deal Master",
  "Performance Tape",
  "Deal Monthly Tape",
  "Pool Master",
  "Pool Monthly Collections Tape",
  "Vintage Summary"
]

const MONEY_FORMAT = "#,##0.00"
// Two decimals, as the reviewed workbook has them: a variance of -14.69% reads as -14.7% at
// one decimal, and the tape is read at that precision.
const PERCENT_FORMAT = "0.00%"
const MULTIPLIER_FORMAT = '0.00"x"'
const DATE_FORMAT = "yyyy-mm-dd"
const PERIOD_FORMAT = "yyyy-mm"
const INTEGER_FORMAT = "0"

const HEADER_FILL: ExcelJS.Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F2937" } }
const HEADER_FONT: Partial<ExcelJS.Font> = { bold: true, size: 10, color: { argb: "FFFFFFFF" } }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

// Vintage Summary's cohort-age columns: M0, M1, ... Money, like every other cash column,
// but the header carries no word for the rules below to match on.
const COHORT_AGE = /^M\d+$/

// Columns whose name alone determines the format. The first match wins, so these run
// most specific first: "MOIC to Date" is a multiplier, not a date, and "Months Since
// Advance Deployment" is a count, not money.
const FORMAT_RULES: { matches: (column: string) => boolean; format: string }[] = [
  { matches: (c) => c.startsWith("MOIC") || c.endsWith("Speed Index"), format: MULTIPLIER_FORMAT },
  { matches: (c) => c.endsWith("Percentage") || c.endsWith("%"), format: PERCENT_FORMAT },
  { matches: (c) => c === "Reporting Period" || c.endsWith("Deployment Month"), format: PERIOD_FORMAT },
  { matches: (c) => c.endsWith("Date"), format: DATE_FORMAT },
  // "Recordings" covers Pool Master's scope counts (Recouping Recordings, Excluded
  // Recordings); "In Scope" covers Recordings In Scope. All are counts, not money.
  {
    matches: (c) =>
      c.includes("Months") ||
      c.endsWith("Count") ||
      c.endsWith("Number") ||
      c.endsWith("Priority") ||
      c.endsWith("In Scope") ||
      c.endsWith("Recordings"),
    format: INTEGER_FORMAT
  },
  // Identifiers are left on General: they render the same, and Deal ID sits next to
  // Artist Spotify ID, which is text and must not be told it is a number.
  {
    matches: (c) =>
      c.includes("Amount") ||
      c.includes("Balance") ||
      c.includes("Collections") ||
      c.includes("Cash") ||
      c.includes("Revenue") ||
      c.includes("Applied") ||
      c.includes("Payable") ||
      c.includes("Recoupment") ||
      c.includes("Advance") ||
      COHORT_AGE.test(c),
    format: MONEY_FORMAT
  }
]

const formatFor = (column: string): string | undefined => FORMAT_RULES.find((r) => r.matches(column))?.format

// Run Info's Value column carries whole paragraphs explaining the model. Widened rather
// than wrapped they spill across the neighbouring cells and read as truncated, which is
// how those explanations went unread. Past this length a column is wrapped at a fixed
// width instead; row heights are left unset so Excel and Sheets auto-fit the paragraph.
const LONG_TEXT_LENGTH = 90
const LONG_TEXT_WIDTH = 80

const longestIn = (values: unknown[]): number =>
  values.reduce<number>((longest, value) => {
    const length = value === null || value === undefined ? 0 : String(value).length
    return Math.max(longest, length)
  }, 0)

// A width that fits the header without letting long free text stretch the sheet.
const widthFor = (column: string, longestValue: number): number =>
  Math.min(Math.max(column.length + 2, longestValue + 2, 10), 60)

const isDateColumn = (column: string) => {
  const format = formatFor(column)
  return format === DATE_FORMAT || format === PERIOD_FORMAT
}

// Excel wants a Date object to apply a date format; the backend sends ISO strings. Only
// date columns are converted: a stray ISO string elsewhere -- Run Info's report date sits
// in a column called "Value" alongside text and counts -- would otherwise become a date
// cell with no date format, which Excel renders as a serial number.
const coerce = (column: string, value: unknown): unknown => {
  if (value === null || value === undefined || value === "") return null
  if (typeof value === "string" && ISO_DATE.test(value) && isDateColumn(column)) {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(Date.UTC(year, month - 1, day))
  }
  return value
}

const addSheet = (workbook: ExcelJS.Workbook, name: string, rows: Record<string, unknown>[]) => {
  const sheet = workbook.addWorksheet(name)
  if (!rows.length) {
    sheet.addRow(["No rows"])
    return
  }

  // Union of keys rather than the first row's, so a null-only column is never dropped.
  const columns: string[] = []
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!columns.includes(key)) columns.push(key)
    })
  })

  const headerRow = sheet.addRow(columns)
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_FILL
    cell.font = HEADER_FONT
  })
  headerRow.height = 24

  rows.forEach((row) => sheet.addRow(columns.map((column) => coerce(column, row[column]))))

  columns.forEach((column, index) => {
    const worksheetColumn = sheet.getColumn(index + 1)
    const longest = longestIn(rows.map((row) => row[column]))
    const wrapped = longest > LONG_TEXT_LENGTH

    worksheetColumn.width = wrapped ? LONG_TEXT_WIDTH : widthFor(column, longest)

    const format = formatFor(column)
    if (!format && !wrapped) return
    worksheetColumn.eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      if (rowNumber === 1) return
      if (format) cell.numFmt = format
      if (wrapped) cell.alignment = { wrapText: true, vertical: "top" }
    })
  })

  sheet.views = [{ state: "frozen", ySplit: 1 }]
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } }
}

export const buildWorkbook = (payload: PortfolioExportPayload): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = "Kahuna"

  // Follow the agreed order, then append anything the backend adds later so a new sheet
  // still reaches the file instead of being silently dropped.
  const names = [
    ...SHEET_ORDER.filter((n) => n in payload),
    ...Object.keys(payload).filter((n) => !SHEET_ORDER.includes(n))
  ]
  names.forEach((name) => addSheet(workbook, name, payload[name] ?? []))

  return workbook
}

export const buildPortfolioWorkbook = async (payload: PortfolioExportPayload): Promise<Blob> => {
  const workbook = buildWorkbook(payload)
  workbook.created = new Date()

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
}

export const reportDateOf = (payload: PortfolioExportPayload): string => {
  const runInfo = payload["Run Info"] ?? []
  const row = runInfo.find((entry) => entry.Item === "Report Date")
  return typeof row?.Value === "string" ? row.Value : new Date().toISOString().slice(0, 10)
}

export const downloadPortfolioExport = async (payload: PortfolioExportPayload) => {
  const blob = await buildPortfolioWorkbook(payload)
  saveAs(blob, `kahuna_portfolio_export_${reportDateOf(payload)}.xlsx`)
}
